import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Paper,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'

type OnFailureMode = 'fail' | 'continue' | 'skip'
import { Play, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import CodeEditor from './shared/CodeEditor'
import api from '../services/api'
import { translateBackendKey } from '../utils/translateBackendKey'

interface ScriptEditorDialogProps {
  open: boolean
  onClose: () => void
  title: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  timeout?: number
  onTimeoutChange?: (timeout: number) => void
  onFailureMode?: OnFailureMode
  onFailureModeChange?: (value: OnFailureMode) => void
  showContinueOnFailure?: boolean
  repositoryId?: number | null // When provided, injects BORG_UI_ context into test runs
}

export default function ScriptEditorDialog({
  open,
  onClose,
  title,
  value,
  onChange,
  placeholder,
  timeout = 300,
  onTimeoutChange,
  onFailureMode = 'fail',
  onFailureModeChange,
  showContinueOnFailure = false,
  repositoryId,
}: ScriptEditorDialogProps) {
  const { t } = useTranslation()
  const [testRunning, setTestRunning] = useState(false)
  const [testResult, setTestResult] = useState<{
    success: boolean
    stdout: string
    stderr: string
    exit_code: number
    execution_time: number
  } | null>(null)

  const handleTestRun = async () => {
    if (!value || value.trim() === '') {
      return
    }

    setTestRunning(true)
    setTestResult(null)

    try {
      const response = await api.post('/scripts/test', {
        script: value,
        ...(repositoryId != null ? { repository_id: repositoryId } : {}),
      })
      setTestResult(response.data)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setTestResult({
        success: false,
        stdout: '',
        stderr: translateBackendKey(error.response?.data?.detail) || error.message,
        exit_code: -1,
        execution_time: 0,
      })
    } finally {
      setTestRunning(false)
    }
  }

  const handleSave = () => {
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <Button
            variant="outlined"
            startIcon={testRunning ? <CircularProgress size={16} /> : <Play size={16} />}
            onClick={handleTestRun}
            disabled={testRunning || !value || value.trim() === ''}
          >
            {testRunning ? t('scriptEditor.testing') : t('scriptEditor.testRun')}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CodeEditor
            label=""
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            height="400px"
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label={t('scriptEditor.timeoutLabel')}
              type="number"
              value={timeout}
              onChange={(e) => onTimeoutChange?.(parseInt(e.target.value) || 300)}
              helperText={t('scriptEditor.timeoutHint')}
              sx={{ flex: 1 }}
              slotProps={{
                htmlInput: { min: 30, max: 3600 },
              }}
            />
            {showContinueOnFailure && (
              <FormControl sx={{ mt: 1 }}>
                <FormLabel sx={{ fontSize: '0.875rem' }}>
                  {t('scriptEditor.onFailureLabel')}
                </FormLabel>
                <RadioGroup
                  row
                  value={onFailureMode}
                  onChange={(e) => onFailureModeChange?.(e.target.value as OnFailureMode)}
                >
                  <FormControlLabel
                    value="fail"
                    control={<Radio size="small" />}
                    label={t('scriptEditor.onFailureFail')}
                  />
                  <FormControlLabel
                    value="continue"
                    control={<Radio size="small" />}
                    label={t('scriptEditor.onFailureContinue')}
                  />
                  <FormControlLabel
                    value="skip"
                    control={<Radio size="small" />}
                    label={t('scriptEditor.onFailureSkip')}
                  />
                </RadioGroup>
              </FormControl>
            )}
          </Box>

          {testResult && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {testResult.success ? (
                  <>
                    <CheckCircle size={20} color="#4caf50" />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'success.main',
                      }}
                    >
                      {t('scriptEditor.testPassed')}
                    </Typography>
                  </>
                ) : testResult.exit_code === 0 ? (
                  <>
                    <AlertTriangle size={20} color="#ff9800" />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'warning.main',
                      }}
                    >
                      {t('scriptEditor.testWarnings')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <XCircle size={20} color="#f44336" />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'error.main',
                      }}
                    >
                      {t('scriptEditor.testFailed')}
                    </Typography>
                  </>
                )}
                <Chip
                  label={t('scriptEditor.exitCode', { code: testResult.exit_code })}
                  size="small"
                  color={testResult.exit_code === 0 ? 'success' : 'error'}
                  sx={{ ml: 'auto' }}
                />
                <Chip
                  label={`${testResult.execution_time.toFixed(2)}s`}
                  size="small"
                  variant="outlined"
                />
              </Box>

              {testResult.stdout && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    {t('scriptEditor.stdout')}
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: '#1e1e1e',
                      maxHeight: '200px',
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{ m: 0, color: '#d4d4d4', fontFamily: 'inherit', fontSize: 'inherit' }}
                    >
                      {testResult.stdout}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {testResult.stderr && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    {t('scriptEditor.stderr')}
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: '#1e1e1e',
                      maxHeight: '200px',
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{ m: 0, color: '#f48771', fontFamily: 'inherit', fontSize: 'inherit' }}
                    >
                      {testResult.stderr}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('common.buttons.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('scriptEditor.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
